/**
 * CampusScene
 *
 * 3D scene component that sets up the Canvas and renders
 * the campus environment with atmospheric lighting, sky, and fog.
 */

import React, { Suspense, useState, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { Environment, Sky } from '@react-three/drei';
import { CampusLocation, LocationConfig } from '../../../types/spatial';
import { Building3D } from './Building3D';
import { CampusGround } from './CampusGround';
import { CampusControls } from './CampusControls';

export interface CampusSceneProps {
  buildings: LocationConfig[];
  unlockedLocations: CampusLocation[];
  selectedLocation: CampusLocation | null;
  hoveredLocation: CampusLocation | null;
  onBuildingClick: (location: CampusLocation) => void;
  onBuildingHover: (location: CampusLocation | null) => void;
}

const CAMERA_POSITION: [number, number, number] = [0, 50, 50];
const CAMERA_FOV = 45;

const LoadingFallback: React.FC = () => (
  <mesh>
    <planeGeometry args={[1, 1]} />
    <meshBasicMaterial color="#4a4f54" />
  </mesh>
);

/** Fallback UI when WebGL is unavailable or context is lost */
function WebGLFallback({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="w-full h-full flex items-center justify-center bg-theme-bg-secondary">
      <div className="text-center p-8 max-w-md">
        <div className="text-4xl mb-4">
          <svg className="w-12 h-12 mx-auto text-theme-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-theme-text-primary mb-2">
          3D View Unavailable
        </h3>
        <p className="text-sm text-theme-text-secondary mb-4">
          Your browser could not render the 3D campus view. This may be due to hardware limitations or a temporary graphics error.
        </p>
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-theme-accent hover:bg-theme-accent-hover text-white rounded-lg
                     font-medium transition-colors text-sm"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

export const CampusScene: React.FC<CampusSceneProps> = ({
  buildings,
  unlockedLocations,
  selectedLocation,
  hoveredLocation,
  onBuildingClick,
  onBuildingHover,
}) => {
  const [renderKey, setRenderKey] = useState(0);
  const [webglFailed, setWebglFailed] = useState(false);

  const handleCreated = useCallback(() => {
    setWebglFailed(false);
  }, []);

  const handleError = useCallback(() => {
    setWebglFailed(true);
  }, []);

  const handleRetry = useCallback(() => {
    setWebglFailed(false);
    setRenderKey((k) => k + 1);
  }, []);

  if (webglFailed) {
    return <WebGLFallback onRetry={handleRetry} />;
  }

  return (
    <Canvas
      key={renderKey}
      shadows
      camera={{
        position: CAMERA_POSITION,
        fov: CAMERA_FOV,
        near: 0.1,
        far: 500,
      }}
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      onCreated={handleCreated}
      gl={{ failIfMajorPerformanceCaveat: false }}
      onError={handleError}
    >
      <Suspense fallback={<LoadingFallback />}>
        {/* Atmospheric sky */}
        <Sky
          sunPosition={[100, 40, 60]}
          turbidity={6}
          rayleigh={0.5}
          mieCoefficient={0.005}
          mieDirectionalG={0.8}
        />

        {/* Depth fog */}
        <fog attach="fog" args={['#b8c4d0', 70, 160]} />

        {/* Ambient fill -- warm hemisphere light */}
        <hemisphereLight
          args={['#c8d8e8', '#4a5a3a', 0.5]}
        />

        {/* Main sunlight */}
        <directionalLight
          position={[40, 60, 30]}
          intensity={1.2}
          color="#fff8f0"
          castShadow
          shadow-mapSize={[2048, 2048]}
          shadow-camera-far={120}
          shadow-camera-left={-55}
          shadow-camera-right={55}
          shadow-camera-top={55}
          shadow-camera-bottom={-55}
          shadow-bias={-0.0005}
        />

        {/* Subtle fill light from opposite side */}
        <directionalLight
          position={[-30, 20, -20]}
          intensity={0.3}
          color="#c8d8f0"
        />

        {/* Environment for reflections on glass materials */}
        <Environment preset="city" />

        {/* Campus ground and environment */}
        <CampusGround />

        {/* Buildings */}
        {buildings.map((config) => (
          <Building3D
            key={config.id}
            config={config}
            isUnlocked={unlockedLocations.includes(config.id)}
            isSelected={selectedLocation === config.id}
            isHovered={hoveredLocation === config.id}
            onClick={onBuildingClick}
            onHover={onBuildingHover}
          />
        ))}

        {/* Camera controls */}
        <CampusControls />
      </Suspense>
    </Canvas>
  );
};

export default CampusScene;
